import { useState } from "react";
import InputField from "components/fields/InputField";
import { TENANT_CONFIG } from "config/tenants";

import { API_URL } from "config/api";

const TENANT_OPTIONS = Object.entries(TENANT_CONFIG).map(([value, { label }]) => ({
  value,
  label,
}));

export default function RegisterForm({ onSwitchToSignIn }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenant, setTenant] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (!tenant) {
        setError("Please select a pilot region.");
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          username,
          email,
          password,
          tenant,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Registration failed");
      }
      setSuccess("Account created! Redirecting to sign in...");
      setTimeout(() => {
        onSwitchToSignIn(username);
        setSuccess("");
        setFirstName(""); setLastName("");
        setUsername(""); setEmail(""); setPassword(""); setTenant("");
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
        Create Account
      </h4>
      <p className="mb-6 ml-1 text-base text-gray-600">
        Fill in your details to get started.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="mb-3 flex gap-3">
          <InputField
            variant="auth"
            label="First Name*"
            placeholder="John"
            id="reg-first-name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <InputField
            variant="auth"
            label="Last Name"
            placeholder="Doe"
            id="reg-last-name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
        <InputField
          variant="auth"
          extra="mb-3"
          label="Username*"
          placeholder="johndoe"
          id="reg-username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <InputField
          variant="auth"
          extra="mb-3"
          label="Email*"
          placeholder="john@example.com"
          id="reg-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <InputField
          variant="auth"
          extra="mb-3"
          label="Password*"
          placeholder="Min. 6 characters"
          id="reg-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <div className="mb-3">
          <label
            htmlFor="reg-tenant"
            className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white"
          >
            Pilot Region*
          </label>
          <select
            id="reg-tenant"
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            className="mt-2 flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:!border-white/10 dark:bg-navy-800 dark:text-white"
          >
            <option value="" disabled>Select your region</option>
            {TENANT_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {error && (
          <p className="mb-3 text-sm font-medium text-red-500">{error}</p>
        )}
        {success && (
          <p className="mb-3 text-sm font-medium text-green-500">{success}</p>
        )}

        <button
          type="submit"
          disabled={loading || !!success}
          className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
        >
          {loading ? "Creating account..." : "Create Account"}
        </button>
      </form>

      <div className="mt-4">
        <span className="text-sm font-medium text-navy-700 dark:text-gray-600">
          Already have an account?
        </span>
        <button
          onClick={() => onSwitchToSignIn()}
          className="ml-1 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
        >
          Sign in
        </button>
      </div>
    </>
  );
}
